import React from 'react';
import { Checkbox } from '../../ui/checkbox';
import { Label } from '../../ui/label';
import { Button } from '../../ui/button';
import { Plus, Trash2, Plane, Hotel } from 'lucide-react';
import DateInput from './DateInput';

const StopEntry = ({ stop, index, onChange, onRemove, testIdPrefix }) => {
  const isHotel = stop.type !== 'travel';
  return (
    <div className="flex items-start gap-2 p-2 bg-slate-900/40 rounded-md border border-slate-700/40">
      <div className="flex-1 space-y-2">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-0.5 p-0.5 bg-slate-700/50 rounded-md">
            <button type="button" onClick={() => onChange(index, { ...stop, type: 'travel' })}
              className={`flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium transition-all ${stop.type === 'travel' ? 'bg-amber-600 text-white' : 'text-slate-400 hover:text-white'}`}
              data-testid={`${testIdPrefix}-${index}-type-travel`}>
              <Plane className="h-3 w-3" /> Travel
            </button>
            <button type="button" onClick={() => onChange(index, { ...stop, type: 'hotel' })}
              className={`flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium transition-all ${isHotel ? 'bg-amber-600 text-white' : 'text-slate-400 hover:text-white'}`}
              data-testid={`${testIdPrefix}-${index}-type-hotel`}>
              <Hotel className="h-3 w-3" /> Hotel
            </button>
          </div>
          {!isHotel && (
            <DateInput contextDate={dateContext} value={stop.date || ''} onChange={(e) => onChange(index, { ...stop, date: e.target.value })}
              className="bg-slate-900 border-slate-600 text-white h-8 text-xs flex-1" data-testid={`${testIdPrefix}-${index}-date`} />
          )}
          <button type="button" onClick={() => onRemove(index)} className="text-slate-500 hover:text-red-400 p-1 transition-colors" data-testid={`${testIdPrefix}-${index}-remove`}>
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
        <input value={stop.description || ''} onChange={(e) => onChange(index, { ...stop, description: e.target.value })} placeholder="Route (e.g., Home to Lowestoft)"
          className="flex h-8 w-full rounded-md border bg-slate-900 border-slate-600 text-white placeholder:text-slate-500 px-2 py-1 text-xs" data-testid={`${testIdPrefix}-${index}-description`} />
        {isHotel && (
          <>
            <div className="grid grid-cols-2 gap-2">
              <input value={stop.hotel_name || ''} onChange={(e) => onChange(index, { ...stop, hotel_name: e.target.value })} placeholder="Hotel name"
                className="flex h-8 w-full rounded-md border bg-slate-900 border-slate-600 text-white placeholder:text-slate-500 px-2 py-1 text-xs" data-testid={`${testIdPrefix}-${index}-hotel-name`} />
              <input value={stop.location || ''} onChange={(e) => onChange(index, { ...stop, location: e.target.value })} placeholder="Location"
                className="flex h-8 w-full rounded-md border bg-slate-900 border-slate-600 text-white placeholder:text-slate-500 px-2 py-1 text-xs" data-testid={`${testIdPrefix}-${index}-location`} />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-0.5">
                <p className="text-[10px] text-slate-500">Check-in</p>
                <DateInput contextDate={dateContext} value={stop.date || ''} onChange={(e) => onChange(index, { ...stop, date: e.target.value })}
                  className="bg-slate-900 border-slate-600 text-white h-8 text-xs" data-testid={`${testIdPrefix}-${index}-date`} />
              </div>
              <div className="space-y-0.5">
                <p className="text-[10px] text-slate-500">Check-out</p>
                <DateInput contextDate={dateContext} value={stop.checkout_date || ''} onChange={(e) => onChange(index, { ...stop, checkout_date: e.target.value })}
                  className="bg-slate-900 border-slate-600 text-white h-8 text-xs" data-testid={`${testIdPrefix}-${index}-checkout-date`} />
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const YesNoToggle = ({ label, value, onChange, testId }) => (
  <div className="flex items-center justify-between py-1.5">
    <Label className="text-xs text-slate-300">{label}</Label>
    <div className="flex items-center gap-1 p-0.5 bg-slate-700/50 rounded-md">
      <button type="button" onClick={() => onChange(true)}
        className={`px-3 py-1 rounded text-[11px] font-medium transition-all ${value ? 'bg-amber-600 text-white' : 'text-slate-400 hover:text-white'}`}
        data-testid={`${testId}-yes`}>Yes</button>
      <button type="button" onClick={() => onChange(false)}
        className={`px-3 py-1 rounded text-[11px] font-medium transition-all ${value === false ? 'bg-slate-600 text-white' : 'text-slate-400 hover:text-white'}`}
        data-testid={`${testId}-no`}>No</button>
    </div>
  </div>
);

const StopSection = ({ stops, onAdd, onUpdate, onRemove, testIdPrefix, label }) => (
  <div className="space-y-2 pl-2 border-l-2 border-amber-500/30">
    <p className="text-[11px] text-slate-500">{label}</p>
    {stops.map((stop, idx) => (
      <StopEntry key={stop._key || `${stop.date}-${idx}`} stop={stop} index={idx} onChange={onUpdate} onRemove={onRemove} testIdPrefix={testIdPrefix} />
    ))}
    <Button type="button" variant="outline" size="sm" onClick={onAdd}
      className="w-full border-slate-600 text-slate-400 hover:text-amber-400 hover:border-amber-500/50 border-dashed h-7 text-xs" data-testid={`${testIdPrefix}-add-btn`}>
      <Plus className="h-3 w-3 mr-1" /> Add stop
    </Button>
  </div>
);

const TravelStopsFields = ({ formData, setFormData, preLabel = 'Travel/hotel before trip', postLabel = 'Travel/hotel after trip', dateContext = '' }) => {
  const addStop = (field) => {
    const stops = [...(formData[field] || []), { type: 'hotel', date: '', hotel_name: '', location: '' }];
    setFormData({ ...formData, [field]: stops });
  };
  const updateStop = (field, index, updatedStop) => {
    const stops = [...(formData[field] || [])];
    stops[index] = updatedStop;
    setFormData({ ...formData, [field]: stops });
  };
  const removeStop = (field, index) => {
    const stops = (formData[field] || []).filter((_, i) => i !== index);
    setFormData({ ...formData, [field]: stops });
  };

  return (
    <>
      {/* --- PRE-TRIP --- */}
      <div className="space-y-2 p-3 rounded-lg border border-slate-700/50 bg-slate-800/50">
        <div className="flex items-center gap-2">
          <Checkbox id="pre_travel_stops" checked={formData.pre_travel}
            onCheckedChange={(checked) => {
              const updates = { pre_travel: checked };
              if (!checked) { updates.pre_departure_date = ''; updates.has_pre_uk = false; updates.pre_uk_stops = []; updates.has_pre_abroad = false; updates.pre_abroad_stops = []; }
              setFormData({ ...formData, ...updates });
            }}
            className="border-amber-500 data-[state=checked]:bg-amber-500 h-3.5 w-3.5" data-testid="pre-travel-checkbox" />
          <Label htmlFor="pre_travel_stops" className="text-xs text-slate-300 cursor-pointer">{preLabel}</Label>
        </div>

        {formData.pre_travel && (
          <div className="space-y-2 pt-1">
            <YesNoToggle label="Any UK travel or hotel stays before departure?" value={formData.has_pre_uk}
              onChange={(val) => setFormData({ ...formData, has_pre_uk: val, ...(!val && { pre_uk_stops: [] }) })} testId="pre-uk" />
            {formData.has_pre_uk && (
              <StopSection stops={formData.pre_uk_stops || []} onAdd={() => addStop('pre_uk_stops')}
                onUpdate={(idx, stop) => updateStop('pre_uk_stops', idx, stop)} onRemove={(idx) => removeStop('pre_uk_stops', idx)}
                testIdPrefix="pre-uk-stop" label="UK travel/hotel stops" />
            )}

            <div className="space-y-1 py-1 border-y border-slate-700/30">
              <Label className="text-amber-400 text-xs font-medium">Date Left UK</Label>
              <DateInput contextDate={dateContext} value={formData.pre_departure_date} onChange={(e) => setFormData({ ...formData, pre_departure_date: e.target.value })}
                className="bg-slate-900 border-slate-600 text-white h-8 text-xs" data-testid="pre-departure-date-input" />
            </div>

            <YesNoToggle label="Any travel or hotel stays abroad before joining?" value={formData.has_pre_abroad}
              onChange={(val) => setFormData({ ...formData, has_pre_abroad: val, ...(!val && { pre_abroad_stops: [] }) })} testId="pre-abroad" />
            {formData.has_pre_abroad && (
              <StopSection stops={formData.pre_abroad_stops || []} onAdd={() => addStop('pre_abroad_stops')}
                onUpdate={(idx, stop) => updateStop('pre_abroad_stops', idx, stop)} onRemove={(idx) => removeStop('pre_abroad_stops', idx)}
                testIdPrefix="pre-abroad-stop" label="Abroad travel/hotel stops" />
            )}
          </div>
        )}
      </div>

      {/* --- POST-TRIP --- */}
      <div className="space-y-2 p-3 rounded-lg border border-slate-700/50 bg-slate-800/50">
        <div className="flex items-center gap-2">
          <Checkbox id="post_travel_stops" checked={formData.post_travel}
            onCheckedChange={(checked) => {
              const updates = { post_travel: checked };
              if (!checked) { updates.post_return_date = ''; updates.has_post_abroad = false; updates.post_abroad_stops = []; updates.has_post_uk = false; updates.post_uk_stops = []; }
              setFormData({ ...formData, ...updates });
            }}
            className="border-amber-500 data-[state=checked]:bg-amber-500 h-3.5 w-3.5" data-testid="post-travel-checkbox" />
          <Label htmlFor="post_travel_stops" className="text-xs text-slate-300 cursor-pointer">{postLabel}</Label>
        </div>

        {formData.post_travel && (
          <div className="space-y-2 pt-1">
            <YesNoToggle label="Any travel or hotel stays abroad after leaving?" value={formData.has_post_abroad}
              onChange={(val) => setFormData({ ...formData, has_post_abroad: val, ...(!val && { post_abroad_stops: [] }) })} testId="post-abroad" />
            {formData.has_post_abroad && (
              <StopSection stops={formData.post_abroad_stops || []} onAdd={() => addStop('post_abroad_stops')}
                onUpdate={(idx, stop) => updateStop('post_abroad_stops', idx, stop)} onRemove={(idx) => removeStop('post_abroad_stops', idx)}
                testIdPrefix="post-abroad-stop" label="Abroad travel/hotel stops" />
            )}

            <div className="space-y-1 py-1 border-y border-slate-700/30">
              <Label className="text-amber-400 text-xs font-medium">Date Returned to UK</Label>
              <DateInput contextDate={dateContext} value={formData.post_return_date} onChange={(e) => setFormData({ ...formData, post_return_date: e.target.value })}
                className="bg-slate-900 border-slate-600 text-white h-8 text-xs" data-testid="post-return-date-input" />
            </div>

            <YesNoToggle label="Any UK travel or hotel stays after arrival?" value={formData.has_post_uk}
              onChange={(val) => setFormData({ ...formData, has_post_uk: val, ...(!val && { post_uk_stops: [] }) })} testId="post-uk" />
            {formData.has_post_uk && (
              <StopSection stops={formData.post_uk_stops || []} onAdd={() => addStop('post_uk_stops')}
                onUpdate={(idx, stop) => updateStop('post_uk_stops', idx, stop)} onRemove={(idx) => removeStop('post_uk_stops', idx)}
                testIdPrefix="post-uk-stop" label="UK travel/hotel stops" />
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default TravelStopsFields;
